import { useState, type FormEvent } from 'react';
import type { StoreProduct, StoreSettings } from '@shared/types.ts';
import { extractAsin } from '@shared/amazon.ts';
import { costcoProductUrl, looksLikeUrl } from '@shared/costco.ts';

export interface MatchDraft {
  storeId: StoreProduct['storeId'];
  productId: string;
  title: string;
  lastPrice?: number;
  sizeLabel?: string;
  unitAmount?: number;
  unit?: string;
}

/** Form for "what do we buy at this store": product link/id, price and size. */
export function ProductMatchForm({
  store,
  initial,
  defaultTitle,
  onSave,
  onCancel,
}: {
  store: StoreSettings;
  initial?: StoreProduct;
  defaultTitle: string;
  onSave: (m: MatchDraft) => void;
  onCancel: () => void;
}) {
  const [productInput, setProductInput] = useState(initial?.productId ?? '');
  const [title, setTitle] = useState(initial?.title ?? defaultTitle);
  const [price, setPrice] = useState(initial?.lastPrice != null ? String(initial.lastPrice) : '');
  const [amount, setAmount] = useState(initial?.unitAmount != null ? String(initial.unitAmount) : '');
  const [unit, setUnit] = useState(initial?.unit ?? 'oz');
  const [error, setError] = useState('');
  const isAmazon = store.id === 'amazon' || store.id === 'amazonfresh' || store.id === 'wholefoods';
  const isCostco = store.id === 'costco';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    let productId = productInput.trim();
    if (isAmazon && productId) {
      const asin = extractAsin(productId);
      if (!asin) {
        setError('Paste an Amazon product link, or a 10-character ASIN like B07XJ8C8F5.');
        return;
      }
      productId = asin;
    }
    if (isCostco && looksLikeUrl(productId)) {
      const page = costcoProductUrl(productId);
      if (!page) {
        setError('Paste a product page from Costco Same-Day (sameday.costco.com), or leave this empty.');
        return;
      }
      productId = page;
    }
    const p = price.trim() ? Number(price.replace(/[$,]/g, '')) : undefined;
    if (p != null && (Number.isNaN(p) || p < 0)) {
      setError('Price should be a number, e.g. 5.99');
      return;
    }
    const a = amount.trim() ? Number(amount) : undefined;
    if (a != null && (Number.isNaN(a) || a <= 0)) {
      setError('Size should be a positive number');
      return;
    }
    onSave({
      storeId: store.id,
      productId: productId || title.trim(),
      title: title.trim() || defaultTitle,
      lastPrice: p,
      unitAmount: a,
      unit: a ? unit : undefined,
      sizeLabel: a ? `${a} ${unit}` : undefined,
    });
  };

  const idLabel = isAmazon ? 'Amazon link or ASIN' : isCostco ? 'Costco Same-Day link (optional)' : 'Product';
  return (
    <form className="stack-lg" onSubmit={submit} noValidate>
      <div className="field">
        <label htmlFor={`pid-${store.id}`}>{idLabel}</label>
        <input
          id={`pid-${store.id}`}
          className="input"
          value={productInput}
          onChange={(e) => {
            setProductInput(e.target.value);
            setError('');
          }}
          placeholder={isAmazon ? 'https://www.amazon.com/…/dp/…' : isCostco ? 'https://sameday.costco.com/…' : 'e.g. Kirkland olive oil 2 L'}
          inputMode={isAmazon || isCostco ? 'url' : 'text'}
          autoCapitalize="off"
        />
        {isAmazon && <span className="help">With a product here, this item goes straight into the filled cart.</span>}
        {isCostco && <span className="help">Share → Copy link on the product page. Order status then opens this product instead of a search.</span>}
      </div>
      <div className="field">
        <label htmlFor={`title-${store.id}`}>Name at the store</label>
        <input id={`title-${store.id}`} className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <div className="grid-2">
        <div className="field">
          <label htmlFor={`price-${store.id}`}>Price (optional)</label>
          <input id={`price-${store.id}`} className="input" inputMode="decimal" placeholder="5.99" value={price} onChange={(e) => setPrice(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor={`size-${store.id}`}>Size (optional)</label>
          <div className="row" style={{ gap: 6 }}>
            <input id={`size-${store.id}`} className="input" inputMode="decimal" placeholder="32" value={amount} onChange={(e) => setAmount(e.target.value)} />
            <label htmlFor={`unit-${store.id}`} className="sr-only">
              Unit
            </label>
            <select id={`unit-${store.id}`} className="select" style={{ width: 84 }} value={unit} onChange={(e) => setUnit(e.target.value)}>
              {['oz', 'lb', 'fl oz', 'ml', 'L', 'g', 'kg', 'ct'].map((u) => (
                <option key={u}>{u}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <span className="help small muted">Prices fill in automatically from forwarded receipts. Sizes let the app compare stores fairly.</span>
      {error && (
        <span className="small" role="alert" style={{ color: 'var(--orange-text)' }}>
          {error}
        </span>
      )}
      <div className="row" style={{ gap: 10 }}>
        <button type="submit" className="btn btn-primary grow">
          Save match
        </button>
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
